import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { api, ocrService } from '../../../services/api';
import { storage } from '../../../config/firebase';

// Plain types, constants, and helper functions shared by the payment-form components in
// CreateOrderModal.tsx and EditTransactionModal.tsx. Kept in their own file (rather than
// exported alongside the components) because a file that mixes component exports with
// plain-value exports can't be hot-swapped by React Fast Refresh — editing it forces a
// full page reload instead of a fast in-place update.

// MULTIPLE is a UI-only mode — sub-mode is sent as actual paymentMethod to backend
export type PaymentMethodType = 'BANK_TRANSFER' | 'CHEQUE' | 'CASH' | 'MULTIPLE' | 'NONE' | 'COUPON' | 'UPI' | 'COINS_REDEEM' | 'WALLET';
export type MultipleSubMode = 'BANK_TRANSFER' | 'CHEQUE' | 'CASH' | 'UPI' | 'COUPON';
export type TransferMode = 'NEFT' | 'RTGS' | 'IMPS' | 'CASH_DEPOSIT';

export interface PendingOrder {
    id: string; breedId?: string; totalCost?: number;
    paymentStatus?: string; paymentType?: string; placedAt?: string;
    numUnits?: number; buffaloCount?: number; calfCount?: number;
    location?: string; paidAmount?: number; pendingAmount?: number;
    coinsRedeemed?: number; READY_TO_DELIVER?: boolean; PHASE_1_READY_TO_DELIVER?: boolean;
}
export interface UploadState {
    file: File | null;
    url: string;
    progress: number;
    uploading: boolean;
    error: string;
    previewUrl?: string;
    isUrlOnly?: boolean;
}

export const emptyUpload = (): UploadState => ({ file: null, url: '', progress: 0, uploading: false, error: '' });

export const TRANSFER_MODES: TransferMode[] = ['NEFT', 'RTGS', 'IMPS', 'CASH_DEPOSIT'];

// Display text for a transfer mode value — every mode reads as itself except
// CASH_DEPOSIT, which is stored/sent as a code but shown to admins as "Cash Deposit".
export const transferModeLabel = (mode?: string): string => mode === 'CASH_DEPOSIT' ? 'Cash Deposit' : (mode || '');

// Fetches an already-uploaded image (no local File object to hand OCR directly) via the
// CORS proxy Firebase Storage needs, then runs OCR against it. Used both by FileUpload's
// own View action and by screens that show a big preview of an existing proof image.
export async function extractTextFromExistingImage(url: string): Promise<string | null> {
    try {
        const proxiedUrl = url.includes('cors-612299373064')
            ? url
            : `https://cors-612299373064.asia-south1.run.app/${url}`;
        const response = await api.get(proxiedUrl, { responseType: 'blob' });
        const blob: Blob = response.data;
        const file = new File([blob], 'existing-proof.jpg', { type: blob.type || 'image/jpeg' });
        const result = await ocrService.extractText(file);
        return result?.text || null;
    } catch {
        return null;
    }
}

// ── Firebase upload helper ─────────────────────────────────────────────────────
export async function uploadToFirebase(file: File, path: string, onProgress: (pct: number) => void): Promise<string> {
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type || 'image/jpeg' });
    return new Promise((resolve, reject) => {
        task.on('state_changed',
            snap => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
            reject,
            async () => { try { resolve(await getDownloadURL(task.snapshot.ref)); } catch (e) { reject(e); } }
        );
    });
}

// ── Best-effort extraction of key payment fields from raw OCR text ─────────────
export interface ExtractedPaymentFields {
    bankName?: string;
    transactionDateTime?: string;
    transferMode?: string;
    amount?: string;
    transactionId?: string;
}

// Parse a date extracted from OCR text (e.g. "5 Jul 2026" or "04/07/2026") into
// DD-MM-YYYY. Numeric DD/MM/YYYY is treated as day-first (Indian convention) rather
// than trusting Date's US-style parsing.
export function parseExtractedDateToDdmmyyyy(raw: string): string | null {
    const datePart = raw.split(',')[0].trim();
    const slashMatch = datePart.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (slashMatch) {
        const [, d, m, yRaw] = slashMatch;
        const y = yRaw.length === 2 ? `20${yRaw}` : yRaw;
        return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
    }
    const parsed = new Date(datePart);
    if (isNaN(parsed.getTime())) return null;
    const d = String(parsed.getDate()).padStart(2, '0');
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const y = parsed.getFullYear();
    return `${d}-${m}-${y}`;
}

export function isValidDdmmyyyy(v: string): boolean {
    const val = v.trim();
    if (!val) return false;
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(val);
    if (!m) return false;
    const d = parseInt(m[1], 10);
    const mon = parseInt(m[2], 10);
    const y = parseInt(m[3], 10);
    if (mon < 1 || mon > 12) return false;
    if (d < 1 || d > 31) return false;
    const dateObj = new Date(y, mon - 1, d);
    return dateObj.getFullYear() === y && dateObj.getMonth() === mon - 1 && dateObj.getDate() === d;
}

// Converts the app's DD-MM-YYYY storage format to the YYYY-MM-DD shape a native
// <input type="date"> requires, and back.
export function ddmmyyyyToIso(v: string): string {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v.trim());
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}
export function isoToDdmmyyyy(v: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
    return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
}

export const PAID_BY_OPTIONS = ['Self', 'Family Member'];

export interface PaymentAutoFillSetters {
    setTxAmount?: (v: string | number) => void;
    setTxDate?: (v: string) => void;
    setTransferMode?: (m: TransferMode) => void;
    setPaidBy?: (v: string) => void;
    setUtrNumber?: (v: string) => void; // also used for Cheque Number
    setAccountHolderName?: (v: string) => void; // Bank Holder Name / UPI Holder Name
    setBankName?: (v: string) => void;
    setCashierName?: (v: string) => void;
    setCashierPhone?: (v: string) => void;
}

// Every payment-form field the "Extracted Details" panel can edit, already normalized
// to the shape the form's own inputs expect (ISO date, plain digits, valid TransferMode).
export interface PaymentAutoFillValues {
    txAmount: string;
    txDate: string;
    transferMode: string;
    paidBy: string;
    utrNumber: string;
    accountHolderName: string;
    bankName: string;
    cashierName: string;
    cashierPhone: string;
}

// Field list shown in the modal — driven entirely by whichever payment type is already
// selected on the payment screen (passed in as `activePaymentType`); the type itself is
// never chosen here, only in the form.
// `isCashier` swaps the plain input for the cashier roster picker, exactly as
// EditableField's `isBank` swaps in BankNameInput. Only the NAME field carries
// it: choosing a cashier fills the phone as well, and two pickers for one
// person would let the name and the number drift apart.
export type FieldSpec = { key: keyof PaymentAutoFillValues; label: string; type?: 'text' | 'date' | 'number'; options?: (string | { value: string; label: string })[]; required?: boolean; isCashier?: boolean };
export function fieldsForPaymentType(paymentType: string, paidBy: string, transferMode?: string): FieldSpec[] {
    switch (paymentType) {
        case 'BANK_TRANSFER': {
            // Cash Deposit still shows Paid By/UTR/Bank Name (so they can be captured if
            // available) but doesn't require them — it's a physical deposit slip, so there
            // isn't always a UTR/reference number or account holder to record.
            const isCashDeposit = transferMode === 'CASH_DEPOSIT';
            return [
                { key: 'transferMode', label: 'Transfer Mode', options: TRANSFER_MODES.map(m => ({ value: m, label: transferModeLabel(m) })), required: true },
                { key: 'paidBy', label: 'Paid By', options: PAID_BY_OPTIONS, required: !isCashDeposit },
                { key: 'utrNumber', label: 'UTR Number', required: !isCashDeposit },
                ...(paidBy && paidBy !== 'Self' ? [{ key: 'accountHolderName' as const, label: 'Bank Holder Name', required: !isCashDeposit }] : []),
                { key: 'bankName', label: 'Bank Name', required: !isCashDeposit },
            ];
        }
        case 'UPI':
            return [
                { key: 'paidBy', label: 'Paid By', options: PAID_BY_OPTIONS, required: true },
                { key: 'utrNumber', label: 'UTR / Reference No.', required: true },
                // Account holder name is optional for UPI even when someone else paid —
                // unlike Bank Transfer, where it's mandatory for a Family Member payer.
                ...(paidBy && paidBy !== 'Self' ? [{ key: 'accountHolderName' as const, label: 'UPI Holder Name', required: false }] : []),
            ];
        case 'CASH':
            return [
                { key: 'cashierName', label: 'Cashier Name', required: true, isCashier: true },
                { key: 'cashierPhone', label: 'Cashier Phone', required: true },
            ];
        case 'CHEQUE':
            return [{ key: 'utrNumber', label: 'Cheque Number', required: true }];
        default:
            return [];
    }
}

// Commits whichever fields are non-empty (extracted or hand-typed) onto the payment form.
export function applyAutoFillValues(values: PaymentAutoFillValues, setters: PaymentAutoFillSetters) {
    if (values.txAmount) setters.setTxAmount?.(values.txAmount);
    if (values.txDate) setters.setTxDate?.(values.txDate);
    if (values.transferMode && (TRANSFER_MODES as string[]).includes(values.transferMode)) {
        setters.setTransferMode?.(values.transferMode as TransferMode);
    }
    if (values.paidBy) setters.setPaidBy?.(values.paidBy);
    if (values.utrNumber) setters.setUtrNumber?.(values.utrNumber);
    if (values.accountHolderName) setters.setAccountHolderName?.(values.accountHolderName);
    if (values.bankName) setters.setBankName?.(values.bankName);
    if (values.cashierName) setters.setCashierName?.(values.cashierName);
    if (values.cashierPhone) setters.setCashierPhone?.(values.cashierPhone);
}

// Resets whatever fields a previous Auto Fill may have populated — used when the
// uploaded image is replaced, since those values no longer match the new document.
export function clearAutoFilledFields(setters: PaymentAutoFillSetters) {
    setters.setUtrNumber?.('');
    setters.setBankName?.('');
    setters.setTxAmount?.('');
    setters.setTxDate?.('');
    setters.setTransferMode?.('NEFT');
}

// ── Validations (matching animalkart mobile app) ───────────────────────────────
export const validateUTR = (v: string) => {
    const val = v.trim();
    if (!val) return 'UTR / Reference number is required';
    if (!/^[A-Za-z0-9]+$/.test(val)) return 'UTR / Reference number must contain only letters and numbers';
    if (val.length < 12) return 'UTR / Reference number must be at least 12 characters';
    return null;
};
export const validateChequeNumber = (v: string) => {
    const val = v.trim();
    if (!val) return 'Cheque number is required';
    if (!/^\d+$/.test(val)) return 'Cheque number must contain only numbers';
    if (val.length < 6 || val.length > 30) return 'Cheque number must be between 6 and 30 digits';
    return null;
};
export const validateAccountHolderName = (v: string) => {
    if (!v.trim()) return 'Account holder name is required';
    if (!/^[A-Za-z ]{3,}$/.test(v.trim())) return 'Enter account holder name (minimum 3 characters, letters only)';
    return null;
};
export const validateBankName = (v: string) => {
    if (!v.trim()) return 'Bank name is required';
    if (!/^[A-Za-z ]{3,}$/.test(v.trim())) return 'Enter bank name (minimum 3 characters)';
    return null;
};
